import { trackEvent } from '@repo/common/analytics';
import { UserRole } from '@repo/common/enums/UserRole';
import { useMount } from 'ahooks';
import { observer } from 'mobx-react-lite';
import { Helmet } from 'react-helmet';

import { useNavigate } from 'react-router';
import { useAuthUser, useSuspenseFeatureAccess } from '../../api';
import { EmptyState } from '../../components/EmptyState';
import { FeatureGuard } from '../../components/FeatureGuard/FeatureGuard';
import {
	PageLayoutContent,
	PageLayoutContentWrapper,
	PageLayoutOuterWrapper,
	PageLayoutWrapper,
} from '../../components/PageLayout';
import { AiImpactDashboardController } from '../../features/AiImpactDashboard/AiImpactDashboardController';
import { useOverviewMetricDatetime } from '../../features/Overview/hooks/useMetricOverviewDatetime';
import { useFeatureFlags } from '../../utils/featureFlags';
import { AiImpactDashboardPageNavBar } from './components/AiImpactDashboardPageNavBar';

function AiImpactDashboardPage() {
	const { aiImpactDashboard } = useFeatureFlags();
	const { analyticsAccess } = useSuspenseFeatureAccess();
	const { user, workspace } = useAuthUser();
	const { dateFilter } = useOverviewMetricDatetime({
		cacheKey: 'ai_impact_dashboard_global',
	});
	const navigate = useNavigate();
	const handleBackButtonClick = () => {
		navigate('/analytics');
	};

	useMount(() => {
		trackEvent(
			'ai_impact_dashboard/page_viewed',
			{
				default_date_filter: dateFilter ?? '30d',
				feature_flag_enabled: aiImpactDashboard ? 'true' : 'false',
				user_role: user?.role ?? '',
				workspace_id: workspace?.id ?? '',
			},
			user,
			workspace
		);
	});

	if (!aiImpactDashboard) {
		return (
			<EmptyState
				title="AI Analytics is not yet enabled"
				description="Please contact your administrator to enable AI Analytics"
				illustrationName="ai"
				includeGoBack={true}
				size="lg"
				buttons={[
					{
						name: 'Go back',
						action: () => {
							handleBackButtonClick();
						},
						size: 'md',
						isPrimary: false,
					},
				]}
			/>
		);
	}

	return (
		<FeatureGuard
			featureName="Analytics"
			v1AllowedRoles={[UserRole.ADMIN, UserRole.EDITOR]}
			v2Permission="Analytics.Read"
			isFeatureIncludedInPlan={analyticsAccess ?? false}
			isAlwaysIncludedFeature={false}
			description="Understand analytics and impact of AI on your workspace."
		>
			<PageLayoutOuterWrapper>
				<Helmet>
					<title>AI Analytics</title>
				</Helmet>
				<PageLayoutWrapper name="ai-analytics">
					<PageLayoutContentWrapper name="ai-analytics">
						<AiImpactDashboardPageNavBar />
						<PageLayoutContent>
							<AiImpactDashboardController />
						</PageLayoutContent>
					</PageLayoutContentWrapper>
				</PageLayoutWrapper>
			</PageLayoutOuterWrapper>
		</FeatureGuard>
	);
}

const ObservedAiImpactDashboardPage = observer(AiImpactDashboardPage);
export default ObservedAiImpactDashboardPage;
